import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ComputedRef, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable, Tag } from '@rygen/rygen-ui/model';

import api from '@/api';

import { useCurrentUserStore } from './CurrentUser';
import { useParentEntityStore } from './ParentEntity';

export interface PerformLoadOptions {
  bypassCache?: boolean;
}

export interface TagStore {
  deleteTag: (tag: Tag) => Promise<boolean>;
  loadTags: (opts?: PerformLoadOptions) => Promise<void>;
  tags: Ref<Loadable<Tag[]>>;
  tagOptions: Ref<Loadable<string[]>>;
}

const PERFORM_LOAD_DEFAULT_OPTIONS = {
  bypassCache: false,
};

const _useTagStore = defineStore('tag', (): TagStore => {
  const { selectedDomainId } = useCurrentUserStore();
  const { t } = useI18n();
  const { includeSubDomains } = useParentEntityStore();
  const toast = useToast();

  const tags: Ref<Loadable<Tag[]>> = ref({
    loading: false,
  });

  const tagOptions: ComputedRef<Loadable<string[]>> = computed(() => ({
    loading: tags.value.loading,
    data: [...new Set(tags.value.data?.map((tag: Tag) => tag.name!!) ?? [])],
    error: tags.value.error,
  }));

  const deleteTag = async (tagObject: Tag): Promise<boolean> => {
    try {
      await api.tag.delete(tagObject);

      if (tags.value.data === undefined) {
        return false;
      }

      // Find and remove the tag by name
      const index = tags.value.data.findIndex(
        (tag: Tag) => tag.name === tagObject.name,
      );
      if (index > -1) {
        tags.value.data.splice(index, 1);
      }

      toast.addSuccess({
        detail: t('messages.successDeletingParam', {
          param: t('tags.tags'),
        }),
      });
    } catch (error) {
      toast.addError({
        detail: t('errors.deletingParam', {
          param: t('tags.tags'),
        }),
        error: error,
      });
      return false;
    }
    return true;
  };

  const loadTags = async (opts: PerformLoadOptions = {}) => {
    const options: Required<PerformLoadOptions> = {
      ...PERFORM_LOAD_DEFAULT_OPTIONS,
      ...opts,
    };

    const numTags = tags.value.data?.length ?? 0;
    const isLoading = tags.value.loading;
    const requiresLoad = numTags === 0 && !isLoading;

    if (options.bypassCache || requiresLoad) {
      return performLoad();
    }
  };

  const performLoad = async () => {
    try {
      tags.value.loading = true;
      tags.value.data = await api.tag.findAll();
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      tags.value.error = e;
      console.error('Error fetching tags', e);
      toast.addError({
        detail: t('errors.loadingParam', { param: t('tags.tags', 2) }),
        error: e,
      });
    } finally {
      tags.value.loading = false;
    }
  };

  const reset = () => {
    tags.value.data = [];
  };

  watch(selectedDomainId, () => {
    reset();
  });

  watch(includeSubDomains, () => {
    reset();
  });

  return {
    deleteTag,
    loadTags,
    tagOptions,
    tags,
  };
});

export const useTagStore = (): TagStore => {
  const store = _useTagStore();
  const { tags, tagOptions } = storeToRefs(store);
  const { loadTags, deleteTag } = store;

  return {
    deleteTag,
    loadTags,
    tagOptions,
    tags,
  };
};
